import './index.less';
import { useCallback, useEffect, useRef, useState } from 'react';

function loadSingleImage(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      resolve(image);
    };
    image.src = url;
  });
}

let canvasContext;

export default function SlidePlayer({ urls = [], waitAll, onLoad }) {
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(0);
  const [images, setImages] = useState(urls.map(() => undefined));
  const currentImage = useRef(null);
  const canvasRef = useRef(null);
  const rafId = useRef(null);
  useEffect(() => {
    async function loadImage() {
      setIsLoading(true);
      let loadedImages = [];
      const imageLoadPromiseArray = [];
      let firstLoadUrls = urls;
      if (!waitAll) {
        firstLoadUrls = [urls[0]];
      }
      firstLoadUrls.forEach((url, index) => {
        if (index === 0) {
          imageLoadPromiseArray.push(
            loadSingleImage(url).then((image) => {
              if (canvasRef.current) {
                const { naturalWidth, naturalHeight } = image;
                let ratio = (naturalWidth / naturalHeight).toFixed(2);
                if (naturalWidth < naturalHeight) {
                  ratio = (naturalHeight / naturalWidth).toFixed(2);
                }
                let scale = 100;
                if (ratio >= 4) {
                  scale = 300;
                } else if (ratio >= 3) {
                  scale = 400;
                } else if (ratio >= 2) {
                  scale = 500;
                } else if (ratio >= 1) {
                  scale = 1000;
                }
                if (naturalWidth >= naturalHeight) {
                  canvasRef.current.width = ratio * scale;
                  canvasRef.current.height = scale;
                } else {
                  canvasRef.current.width = scale;
                  canvasRef.current.height = ratio * scale;
                }
                onLoad && onLoad();
              }
              return image;
            }),
          );
        } else {
          imageLoadPromiseArray.push(loadSingleImage(url));
        }
      });
      loadedImages = await Promise.all(imageLoadPromiseArray);
      setImages(loadedImages);
      currentImage.current = loadedImages[0];
      canvasDrawImage();
      setIsLoading(false);
    }
    if (urls?.length > 0) {
      loadImage();
    } else {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      canvasContext = null;
    }
    setCurrent(0);
    setTotal(urls.length);

    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      canvasContext = null;
      setImages((images) => {
        for (let i = 0; i < images.length; i++) {
          if (images[i]) {
            images[i] = null;
          }
        }
        return [];
      });
    };
  }, [urls, waitAll]);

  const canvasDrawImage = useCallback(() => {
    if (!canvasContext) {
      canvasContext = canvasRef.current.getContext('2d');
    }
    const image = currentImage.current;
    if (image) {
      canvasContext.drawImage(image, 0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    rafId.current = requestAnimationFrame(() => {
      canvasDrawImage();
    });
  }, []);

  async function selectImage(index) {
    const imagesCopy = [...images];
    let image = imagesCopy[index];
    if (!image) {
      image = await loadSingleImage(urls[index]);
      imagesCopy[index] = image;
    }
    setImages(imagesCopy);
    currentImage.current = image;
  }

  function toFirst() {
    const index = 0;
    setCurrent(index);
    selectImage(index);
  }

  function toLast() {
    const index = urls.length - 1;
    setCurrent(index);
    selectImage(index);
  }

  function toNext() {
    let index = current;
    if (index >= urls.length - 1) {
      index = urls.length - 1;
    } else {
      index++;
    }
    setCurrent(index);
    selectImage(index);
  }

  function toPrevious() {
    let index = current;
    if (index === 0) {
      index = 0;
    } else {
      index--;
    }
    setCurrent(index);
    selectImage(index);
  }

  return (
    <div className="slide-player">
      <canvas ref={canvasRef}></canvas>

      <div className={`loading-mask ${isLoading ? 'show' : ''}`}>
        <div className="loading loading-spinner">111</div>
      </div>

      <div className={`slide-control-bar ${isLoading ? '' : 'show'}`}>
        <button className="btn" onClick={toFirst}>
          ┃ ⬅
        </button>

        <button className="btn" onClick={toPrevious}>
          ⬅ Prev
        </button>

        <span className="countdown">
          {current + 1} / {total}
        </span>

        <button className="btn" onClick={toNext}>
          Next ⮕
        </button>

        <button className="btn" onClick={toLast}>
          ⮕ ┃
        </button>
      </div>
    </div>
  );
}
